import { decodeEntityString } from '@libs/algolia';

import { isPurchaseOptionAvailable } from '../../libs/purchaseAvailability';
import type { DeliveryMethodType, Product, ProductApiResponse } from './dto';
import type { MerchandisingHierarchy, PrimaryCategory, ProductData, ProductVariant } from './types';

type MapVariantProps = {
    sku: string;
    variant: Product;
    webStyleId: string;
    primaryCategory: PrimaryCategory;
    merchandisingHierarchy: MerchandisingHierarchy;
};

const mapVariant = (variantData: MapVariantProps): ProductVariant => {
    const { webStyleId, sku, variant, primaryCategory, merchandisingHierarchy } = variantData;
    const { name, brand, rating, price, description, isLivePet, deliveryMethods, ...restVariantData } = variant;

    // Build delivery method list ['BOPIS', 'SDD', 'STH']
    const deliveryMethodsList = Object.entries(deliveryMethods)
        .reduce((acc, prev) => {
            const [methodName, methodData] = prev;

            if (isPurchaseOptionAvailable(methodData)) acc.push(methodName as DeliveryMethodType);

            return acc;
        }, [] as DeliveryMethodType[])
        .sort((a, b) => a.localeCompare(b));

    const { BOPIS, SDD, STH } = deliveryMethods;
    const isLivePetInStoreOnly =
        isLivePet &&
        !isPurchaseOptionAvailable(BOPIS) &&
        !isPurchaseOptionAvailable(SDD) &&
        !isPurchaseOptionAvailable(STH);

    return {
        ...restVariantData,
        sku,
        price,
        isLivePet,
        webStyleId,
        primaryCategory,
        deliveryMethods,
        deliveryMethodsList: isLivePetInStoreOnly ? ['BOPIS'] : deliveryMethodsList,
        isLivePetInStoreOnly,
        merchandisingHierarchy,
        name: decodeEntityString(name),
        brand: brand ? { url: brand.url, name: decodeEntityString(brand.name) } : null,
        rating: { average: rating?.average ?? 0, count: rating?.count ?? 0 },
        description: description ? decodeEntityString(description) : description,
        media: variant.media,
    };
};

export function mapProductData({ data }: { data: ProductApiResponse['data'] }): ProductData {
    const sizeChartImgUrl = Object.values(data.variants).find(v => v.sizeChartImgUrl)?.sizeChartImgUrl ?? '';
    const variants = Object.entries(data.variants).reduce<Record<string, ProductVariant>>((acc, [sku, variant]) => {
        acc[sku] = mapVariant({
            sku,
            variant,
            webStyleId: data.webStyleId,
            primaryCategory: data.primaryCategory,
            merchandisingHierarchy: data.merchandisingHierarchy,
        });
        return acc;
    }, {});

    return { ...data, variants, variantsSkus: Object.keys(data.variants), sizeChartImgUrl };
}
